import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL, LANG } from "@/config";
import { getLang, t } from "@/lib/i18n";

const Ctx = createContext(null);
const KEY = "woodia-gorunum";
const PARAM = { canli: "live", onizleme: "preview" };

const readPref = () => {
  try {
    const s = sessionStorage.getItem(KEY);
    return s === "live" || s === "preview" ? s : null;
  } catch (e) {
    return null;
  }
};
const writePref = (m) => {
  try {
    sessionStorage.setItem(KEY, m);
  } catch (e) {
    /* storage unavailable */
  }
};
const paramMode = (search) => PARAM[new URLSearchParams(search).get("gorunum")] || null;

export function ContentProvider({ children }) {
  const { search } = useLocation();
  const [state, setState] = useState(() => {
    const snap = typeof window !== "undefined" && window.__WOODIA_CONTENT__;
    return snap
      ? { content: snap.content, site: snap.site, notices: snap.notices || [], isAdmin: false, error: false, ready: true }
      : { content: null, site: null, notices: [], isAdmin: false, error: false, ready: false };
  });
  const [pref, setPref] = useState(() => paramMode(search) || readPref());

  const reload = useCallback(
    () =>
      axios
        .get(`${API}/content`, { params: { lang: LANG }, withCredentials: true })
        .then((r) => setState({ content: r.data.content, site: r.data.site, notices: r.data.notices || [], isAdmin: !!r.data.viewer?.admin, error: false, ready: true }))
        .catch(() => setState((s) => ({ ...s, error: !s.content, ready: true }))),
    []
  );

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const p = paramMode(search);
    if (p) {
      writePref(p);
      setPref(p);
    }
  }, [search]);

  const setMode = useCallback((m) => {
    writePref(m);
    setPref(m);
  }, []);

  const mode = state.isAdmin ? pref || "preview" : "live";
  const value = useMemo(() => ({ ...state, enLive: !!state.site?.en_live, mode, isPreview: mode === "preview", setMode, reload }), [state, mode, setMode, reload]);
  return createElement(Ctx.Provider, { value }, children);
}

export const useSite = () => useContext(Ctx);

export const assetUrl = (src) => (src && src.startsWith("/api/") ? `${BACKEND_URL}${src}` : src);

export function isFilled(v) {
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  return true;
}

// filled: has a value; usable: may render the real value in this mode; show: render the slot at all
// EN: translatable fields carry f.en; EN is live only if TR is live AND EN is verified
function localize(f) {
  if (getLang() !== "en" || !f || !("en" in f)) return f;
  return { value: f.en?.value ?? null, verified: !!(f.verified && isFilled(f.value) && f.en?.verified) };
}

export function fieldState(raw, isPreview) {
  const f = localize(raw);
  const filled = !!f && isFilled(f.value);
  const verified = !!f?.verified;
  return { filled, verified, value: filled ? f.value : null, usable: filled && (isPreview || verified), show: isPreview || (filled && verified) };
}

export function getCoords(c, isPreview) {
  const f = c?.location?.coordinates;
  const v = f?.value;
  const ok = !!v && typeof v.lat === "number" && typeof v.lng === "number";
  return ok && (isPreview || f.verified) ? v : null;
}

export function toIntl(raw) {
  const d = raw.replace(/\D/g, "");
  if (raw.trim().startsWith("+")) return d;
  if (d.startsWith("00")) return d.slice(2);
  if (d.startsWith("0")) return `90${d.slice(1)}`;
  if (d.length === 10) return `90${d}`;
  return d;
}

export function getLinks(c, isPreview) {
  const use = (f) => (fieldState(f, isPreview).usable ? String(f.value).trim() : null);
  const phone = use(c?.contact?.phone);
  const wa = use(c?.contact?.whatsapp);
  const ig = use(c?.contact?.instagram);
  const dirUrl = use(c?.location?.directions_url);
  const coords = getCoords(c, isPreview);
  const igHandle = ig && !/^https?:/i.test(ig) ? ig.replace(/^@/, "") : null;
  const directions = dirUrl || (coords && `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}`);
  return {
    phone: phone && { href: `tel:+${toIntl(phone)}`, label: phone },
    whatsapp: wa && { href: `https://wa.me/${toIntl(wa)}`, label: wa },
    instagram: ig && { href: igHandle ? `https://www.instagram.com/${igHandle}/` : ig, label: igHandle ? `@${igHandle}` : ig },
    directions: directions && { href: directions, label: dirUrl ? t("mapsLabel") : `${coords.lat}, ${coords.lng}` },
  };
}

export function hasAnyLink(links) {
  return Object.values(links).some(Boolean);
}

export function localModule(m) {
  if (getLang() !== "en") return m;
  const en = m.en || {};
  return { ...m, title: en.title || null, body: en.body || null, verified: !!(m.verified && isFilled(m.body) && en.verified) };
}

export function visibleModules(c, isPreview) {
  const list = (c?.modules || []).map(localModule);
  return isPreview ? list : list.filter((m) => m.visible && m.verified && isFilled(m.body));
}

export function altOf(img, isPreview) {
  if (getLang() !== "en") return img.alt || "";
  const en = img.en || {};
  return (isPreview || en.verified) && isFilled(en.alt) ? en.alt : t("photoAlt");
}

const istanbulToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());

export function noticeStatus(n, lang = getLang()) {
  const today = istanbulToday();
  if (!n.active) return "inactive";
  if (!(lang === "en" ? n.verified_en && n.verified_tr : n.verified_tr) || !n.title?.[lang]) return "unverified";
  if (today < n.start_date || today > n.end_date) return "outOfRange";
  return "live";
}

export function pickNotices(notices, isPreview) {
  const list = [...(notices || [])].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  if (isPreview) return list;
  const live = list.find((n) => noticeStatus(n) === "live");
  return live ? [live] : [];
}

export function showMedia(m, isPreview) {
  return !!m && m.visible !== false && isFilled(m.src) && (isPreview || !m.stock);
}

export function visibleGallery(c, isPreview) {
  return (c?.gallery || []).filter((g) => showMedia(g, isPreview));
}

export function groupFormEnabled(c) {
  return !!c?.forms?.group_request?.enabled;
}

export function privacyVisible(c, isPreview) {
  return isPreview || fieldState(c?.privacy?.text, false).show;
}

export function operatorVisible(c, isPreview) {
  return isPreview || Object.values(c?.operator || {}).some((f) => fieldState(f, false).show);
}
